import React, { Suspense } from 'react'
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, parsePath, Route } from 'react-router-dom'

import * as Sentry from '@sentry/react'

import { micrositesMetadataList } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/constants'
import { PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { HostWrapper } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/hostWrapper/HostWrapper'

import { NavigateListener, UNSAFE_listenBeforeNavigate, UNSAFE_patchRouterNavigate } from './patchRouterNavigate'
import { PreviousRouteProvider } from './PreviousRouteProvider'
import usePathChanged from './usePathChanged'
import { MainAppLayout } from '../../common/components/appLayout/MainAppLayout'
import RouterErrorBoundaryWrapper from '../../common/components/error/ErrorBoundaryWrapper/RouterErrorBoundaryWrapper'
import InactivityWarningScreen from '../../common/components/inactivityWarningScreen/InactivityWarningScreen'
import { STICKY_QUERY_PARAMS } from '../../common/constants/appConstants'
import { preserveSearchParams } from '../../common/utils/routingUtils'
import { DevToolsModalWrapper } from '../devTools/DevToolsModalWrapper'
import { LyraWebIntlProvider } from '../intl/LyraWebIntlProvider'
import { MicrositeRoutes } from '../microsite/micrositeRoutes'

const RegisterRoutes = React.lazy(() => import(/* webpackChunkName: "Register" */ '../register/registerRoutes'))
const MobileRoutes = React.lazy(() => import(/* webpackChunkName: "Mobile" */ './mobileRoutes'))
const SecureRoutes = React.lazy(() => import(/* webpackChunkName: "Secure" */ './secureRoutes'))

const ContactUs = React.lazy(() => import(/* webpackChunkName: "ContactUs" */ '../chatLive/ContactUs'))
const FaqView = React.lazy(() => import(/* webpackChunkName: "FaqView" */ '../faq/FaqView'))
const ForgotPasswordContainer = React.lazy(
  () => import(/* webpackChunkName: "ForgotPassword" */ '../forgotPasswordContainer/ForgotPasswordContainer'),
)
const ResetPasswordContainer = React.lazy(
  () => import(/* webpackChunkName: "ResetPassword" */ '../resetPasswordContainer/ResetPasswordContainer'),
)
const ChangeEmailConfirmationContainer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ChangeEmailConfirmation" */ '../editProfile/changeEmail/ChangeEmailConfirmationContainer'
    ),
)

const Login = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Login" */
      /* webpackPrefetch: true  */
      '../login/Login'
    ),
)
const SatisfactionSurvey = React.lazy(
  () => import(/* webpackChunkName: "SatisfactionSurvey" */ '../satisfactionSurvey/SatisfactionSurvey'),
)
const SatisfactionSurveyConfirmation = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SatisfactionSurveyConfirmation" */ '../satisfactionSurvey/SatisfactionSurveyConfirmation'
    ),
)
const NeurodiversityCenterOfExcellenceContainer = React.lazy(() =>
  import(
    /* webpackChunkName: "NeurodiversityCenterOfExcellenceContainer" */ '../neurodiversityCenterOfExcellence/NeurodiversityCenterOfExcellenceContainer'
  ).then((module) => ({ default: module.NeurodiversityCenterOfExcellenceContainer })),
)
const HealthChallengesToolkitContainer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HealthChallengesToolkitContainer" */ '../healthChallengesToolkit/HealthChallengesToolkitContainer'
    ),
)
const WomensHealthToolkitContainer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "WomensHealthToolkitContainer" */ '../womensHealthToolkit/WomensHealthToolkitContainer'
    ),
)
const VerifyAttestation = React.lazy(
  () => import(/* webpackChunkName: "VerifyAttestation" */ '../verifyAttestation/VerifyAttestation'),
)

const HandoffExternalEligibility = React.lazy(
  () =>
    import(/* webpackChunkName: "HandoffExternalEligibility" */ '../externalEligibility/HandoffExternalEligibility'),
)

const WorkLifeServicesContactCareInternational = React.lazy(
  () =>
    import(
      /* webpackChunkName: "WorkLifeServicesContactCareInternational" */ '../workLifeServicesContactCareInternational/WorkLifeServicesContactCareInternational'
    ),
)

const ExchangeHandshakeTokenContainer = React.lazy(() =>
  import(
    /* webpackChunkName: "ExchangeHandshakeTokenContainer" */ '../connectedService/ExchangeHandshakeTokenContainer'
  ).then((module) => ({ default: module.ExchangeHandshakeTokenContainer })),
)

const SudConsentLandingPage = React.lazy(
  () => import(/* webpackChunkName: "SudConsentLandingPage" */ '../sudConsent/SudConsentLandingPage'),
)

const DbtReferralPage = React.lazy(
  () => import(/* webpackChunkName: "DbtReferralPage" */ '../dbtReferral/DbtReferralPage'),
)

const CMSMicrositeRouter = React.lazy(() =>
  import(/* webpackChunkName: "CMSMicrositeRouter" */ '../CMSMicrositeWithMetadata/CMSMicrositeRouter').then(
    (module) => ({ default: module.CMSMicrositeRouter }),
  ),
)

const ElectionMicrosite = React.lazy(
  () => import(/* webpackChunkName: "ElectionMicrosite" */ '../electionMicrosite/ElectionMicrosite'),
)

const CustomerSpecificInstanceRedirectionContainer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CustomerSpecificInstanceRedirection" */ '../microsite/components/customerSpecificInstanceRedirection/CustomerSpecificInstanceRedirectionContainer'
    ),
)

const EssentialsHomepageWidgetContainer = React.lazy(() =>
  import(/* webpackChunkName: "EssentialsHomepageWidget" */ '../pathways/EssentialsHomepageWidgetContainer').then(
    (module) => ({ default: module.EssentialsHomepageWidgetContainer }),
  ),
)

const ForkedTriage = React.lazy(
  () => import(/* webpackChunkName: "ForkedTriage" */ '../pathways/pageWrappers/ForkedTriage'),
)

const AppleRegistrationContainer = React.lazy(
  () => import(/* webpackChunkName: "AppleRegistration" */ '../register/AppleRegistrationContainer'),
)

const GoogleRegistrationContainer = React.lazy(
  () => import(/* webpackChunkName: "GoogleRegistration" */ '../register/GoogleRegistrationContainer'),
)

const SelfCareWellnessTopicContainer = React.lazy(
  () =>
    import(/* webpackChunkName: "SelfCareWellnessTopic" */ '../selfCareWellnessTopic/SelfCareWellnessTopicContainer'),
)

const UnsubscribeContainer = React.lazy(() =>
  import(/* webpackChunkName: "Unsubscribe" */ '../unsubscribe/UnsubscribeContainer').then((module) => ({
    default: module.UnsubscribeContainer,
  })),
)

const WellnessCheckInFormContainer = React.lazy(
  () => import(/* webpackChunkName: "WellnessCheckInForm" */ '../wellnessCheckIn/WellnessCheckInFormContainer'),
)

const WellnessCheckInPreviewContainer = React.lazy(
  () => import(/* webpackChunkName: "WellnessCheckInPreview" */ '../wellnessCheckIn/WellnessCheckInPreviewContainer'),
)

const RouterPathChanged = () => {
  usePathChanged()
  return <Outlet />
}

const PreviousRoute = () => {
  return (
    <PreviousRouteProvider>
      <Outlet />
    </PreviousRouteProvider>
  )
}

const OneLyraEnabledRoute = () => {
  // When resolving 1Lyra enabled routes, serve a Fragment during mobile app proxy
  return <></>
}

const uniqueMicrositePaths = Array.from(new Set(micrositesMetadataList.map((m) => m.micrositeUrl)))

const createRootRoutes = () => {
  const mainAppRouteContainer = (
    <HostWrapper>
      <MainAppLayout>
        <Outlet />
        <InactivityWarningScreen />
      </MainAppLayout>
    </HostWrapper>
  )

  const mainAppIntlContainer = (
    <LyraWebIntlProvider>
      <Outlet />
      <DevToolsModalWrapper />
    </LyraWebIntlProvider>
  )

  const mainAppRoutes = (
    <Route element={mainAppRouteContainer}>
      <Route>
        <Route path='establish-connection' element={<ExchangeHandshakeTokenContainer />} />
        <Route path='unsubscribe' element={<UnsubscribeContainer />} />
        <Route path='satisfaction' element={<SatisfactionSurvey />} />
        <Route path='satisfaction/confirmation' element={<SatisfactionSurveyConfirmation />} />
        <Route
          element={
            <>
              <Outlet />
              <Suspense fallback={null}>
                <CustomerSpecificInstanceRedirectionContainer />
              </Suspense>
            </>
          }
        >
          <Route path='login' element={<Login />} />
          <Route path='forgot-password' element={<ForgotPasswordContainer />} />
        </Route>
        <Route path='reset-password' element={<ResetPasswordContainer />} />
        <Route path='reset-pwd' element={<ResetPasswordContainer />} />
        <Route path='faq' element={<FaqView />} />
        <Route path='worklife' element={<></>} />
        <Route path='verify-attestation' element={<VerifyAttestation />} />
        <Route path='confirm-change-email' element={<ChangeEmailConfirmationContainer />} />
        <Route path='handoff-eligibility' element={<HandoffExternalEligibility />} />
      </Route>
      <Route path='register/*' element={<RegisterRoutes />} />
      <Route path='secure/*' element={<SecureRoutes />} />
      <Route path='pathways'>
        <Route path='care-options' element={<ForkedTriage />} />
        <Route path='self-care-options' element={<SelfCareWellnessTopicContainer />} />
        <Route path='essentials-homepage-widget' element={<EssentialsHomepageWidgetContainer />} />
      </Route>
      <Route path='election' element={<ElectionMicrosite />} />
      {uniqueMicrositePaths.map((path) => (
        <Route key={path} path={path} element={<CMSMicrositeRouter />} />
      ))}
      <Route path='wellness-check-in' element={<Navigate to='/wellness-check-in/1' replace />} />
      <Route path='wellness-check-in/preview' element={<WellnessCheckInPreviewContainer />} />
      <Route path='wellness-check-in/:pageIndex' element={<WellnessCheckInFormContainer />} />
      <Route path='center-of-excellence/neurodiversity' element={<NeurodiversityCenterOfExcellenceContainer />} />
      <Route path='health-challenges' element={<HealthChallengesToolkitContainer />} />
      <Route path='womens-health' element={<WomensHealthToolkitContainer />} />
      <Route path='manager' element={<OneLyraEnabledRoute />} />
      <Route path='smart-search' element={<OneLyraEnabledRoute />} />
      <Route path='*!survey' element={<Navigate to='login' replace />} />
      <Route path='worklife-care-team-numbers' element={<WorkLifeServicesContactCareInternational />} />
    </Route>
  )

  const rootRoutes = (
    <>
      <Route element={<RouterErrorBoundaryWrapper />}>
        <Route element={<RouterPathChanged />}>
          <Route element={<PreviousRoute />}>
            {/* /logout redirects to /home */}
            <Route path='home' element={<Navigate to='/' />} />
            {MicrositeRoutes}
            <Route element={mainAppIntlContainer}>
              {mainAppRoutes}
              {/* Google SSO button embedded in an iframe */}
              <Route path={PAGE_ROUTES.SSO_REGISTRATION_GOOGLE} element={<GoogleRegistrationContainer />} />
              {/* Apple SSO button embedded in an iframe */}
              <Route path={PAGE_ROUTES.SSO_REGISTRATION_APPLE} element={<AppleRegistrationContainer />} />
              <Route path='contact-us' element={<ContactUs />} />
              {/* SUD Consent - standalone page accessible via one-time token */}
              <Route path='consent/sud-data-sharing' element={<SudConsentLandingPage />} />
              {/* DBT Referral - public-facing referral */}
              <Route path='refer/dbt' element={<DbtReferralPage />} />
              {/* Mobile */}
              <Route path='lyraweb-mobile/*' element={<MobileRoutes />} />
            </Route>
            <Route path='*' element={<Navigate to='login' replace />} />
          </Route>
        </Route>
      </Route>
    </>
  )

  return rootRoutes
}

// Some monkey business to listen to router navigations
// Preserves app-wide query parameters in the URL across route changes
const preserveSearchParamsListener: NavigateListener = (navigateArgs, { location }) => {
  const [toArg] = navigateArgs
  if (typeof toArg === 'number') {
    return
  }
  let to
  if (typeof toArg === 'string') {
    to = parsePath(toArg)
  } else {
    to = { ...toArg }
  }
  if (location.search) {
    to.search = preserveSearchParams(STICKY_QUERY_PARAMS, location.search, to.search)
    navigateArgs[0] = to
  }
}

export function createRouter() {
  const routes = createRoutesFromElements(createRootRoutes())
  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
  const router = sentryCreateBrowserRouter(routes)

  UNSAFE_patchRouterNavigate(router)
  UNSAFE_listenBeforeNavigate(router, preserveSearchParamsListener)

  return router
}
